// sr-engine.js — Self-Recording concept resolver.
//
// PORT TARGET (iOS): KFESRDBManager.m
//   • analysisData:         ManualSR — walk the explicit Items array
//   • analysisAutomaticData: AutoSR — pull met-WORD pool, shuffle, slice
//
// Per CLAUDE.md Rule #1, this module is pure: it consumes injected
// dependencies (sr-config, mmu-loader, learner-store, item-media-loader) and
// returns a state snapshot. The screen renders the snapshot — engine never
// touches the DOM.
//
// Brief 060 (Mode + display types); Brief 060b (Auto-mode word-keyed met-pool).
//
// NO TOP-UP (Brief 060b §"User clarifications captured in CMS contract §8.1",
// 2026-05-28). iOS tops up a short Met pool from the unrestricted is_sr pool
// (KFESRDBManager.m:383–422). The web port deliberately does NOT — Chris called
// the top-up "an iOS design bug … the design decision should be to limit the
// number of targets to total words met up to that point." So the cap is
// min(numberOfItems, eligible.length) and an empty met-pool yields an empty list
// (the screen's "No items available" state). Do not reintroduce the top-up.
//
// EMPTY POOL IS GENUINE (Brief 180). The met-pool now includes L2B Training drill
// words (item-media-loader.getWordsForItem unions content/item-mmu-words.json —
// see that file's header). So once the learner has completed any L2B drill, the
// pool is populated; an empty Auto pool means the learner genuinely has no met
// words yet (a real cold-start), not the pre-180 bug where drills contributed
// nothing because they carry no master_item_media rows.

import { DISPLAY_TYPE, displayTypeName } from "../data/sr-config.js";

/** @typedef {0 | 1 | 2 | 3} DisplayTypeInt */
/** @typedef {"audio" | "audio_image" | "audio_text" | "audio_image_text"} DisplayTypeName */
/** @typedef {import("../data/mmu-loader.js").Mmu & { displayType: DisplayTypeName }} Concept */
/** @typedef {import("../data/mmu-loader.js").Mmu & { wordIds: number[] }} SrMmu */

/**
 * @typedef {object} SrEngineDeps
 * @property {(itemId: number | string) => import("../data/sr-config.js").SrConfig | undefined} getSrConfig
 * @property {(id: number | string) => import("../data/mmu-loader.js").Mmu | undefined} getMmu
 * @property {(itemId: number, preferHaai?: boolean) => import("../data/mmu-loader.js").Mmu[]} getMmusForItem
 * @property {() => Promise<number[]>} getCompletedItemIds  itemIds the learner has completed (iOS learner_lesson_items.completed = 1)
 * @property {(itemId: number) => number[]} getWordsForItem  word-ids reachable from an item via its media
 * @property {() => SrMmu[]} getAllSrMmus  every SR-eligible MMU, each carrying its constituent wordIds
 */

/**
 * Resolve the concept list for an SR item per its Mode flag.
 *
 *   Manual:    read the explicit Items list from sr-config.json. Each
 *              entry's displayType is what the plist declared; if the
 *              plist omitted it, tools/build-sr-config.mjs already
 *              substituted the item-level default, so entries here are
 *              always populated.
 *   Automatic: mirror the iOS met-WORD query (Brief 060b). Build the set of
 *              word-ids the learner has met — every word reachable from a
 *              completed lesson item via its media (KFEContainerManager.m
 *              temp_words_met). An SR MMU is eligible iff EVERY one of its
 *              constituent words is met (the iOS `mmu_id NOT IN
 *              (MMUs with an unmet word)` predicate — `every`, not `some`).
 *              Shuffle the eligible set and take min(numberOfItems, eligible).
 *              Apply the item's defaultDisplayType to each. Empty pool → empty
 *              list (Brief 060 success criterion #5 — the screen renders the
 *              "No items available" empty state).
 *
 *              The cap is explicit: iOS tops up the shortfall from ALL MMUs
 *              when the met-pool is smaller than NumberOfItems
 *              (KFESRDBManager.m:389–422). Brief 060b deliberately does NOT
 *              mirror that top-up (CMS contract §8.1, user call) — targets are
 *              capped at what the learner has actually met.
 *
 * Falls back to the legacy MMU list (audio-only) when sr-config has no
 * entry for itemId — keeps older items working until the CMS migration
 * is complete.
 *
 * @param {number} itemId
 * @param {SrEngineDeps} deps
 * @returns {Promise<Concept[]>}
 */
export async function resolveConcepts(itemId, deps) {
  const cfg = deps.getSrConfig(itemId);

  if (!cfg) {
    const list = deps.getMmusForItem(itemId, false);
    return list.map((m) => attachDisplayType(m, DISPLAY_TYPE.AUDIO));
  }

  if (cfg.mode === "manual") {
    /** @type {Concept[]} */
    const out = [];
    for (const entry of cfg.items) {
      const mmu = deps.getMmu(entry.mmuId);
      if (!mmu) continue;
      out.push(attachDisplayType(mmu, entry.displayType));
    }
    return out;
  }

  // Automatic: select from the learner's met-WORD pool (iOS canon —
  // KFESRDBManager.m:383 → KFEContainerManager.getMmuForSRWithType).
  const completedItems = await deps.getCompletedItemIds();
  /** @type {Set<number>} */
  const metWords = new Set();
  for (const itemId of completedItems) {
    for (const w of deps.getWordsForItem(itemId)) metWords.add(w);
  }

  /** @type {SrMmu[]} */
  const eligible = [];
  for (const mmu of deps.getAllSrMmus()) {
    const mmuWords = mmu.wordIds ?? [];
    // A wordless MMU can never be "met" — skip it. (iOS would treat it as
    // vacuously eligible via the NOT IN subquery; we exclude it because an
    // SR target with no constituent words has nothing to record against.)
    if (mmuWords.length === 0) continue;
    if (mmuWords.every((w) => metWords.has(w))) eligible.push(mmu);
  }
  if (eligible.length === 0) return [];

  // iOS uses random selection per KFESRDBManager.analysisAutomaticData →
  // KFERandomTools. Match with Math.random; tests stub via _setShuffleForTest.
  const shuffled = shuffle(eligible);
  const cap = Math.min(
    cfg.numberOfItems > 0 ? cfg.numberOfItems : shuffled.length,
    shuffled.length,
  );
  return shuffled.slice(0, cap).map((m) => attachDisplayType(m, cfg.defaultDisplayType));
}

/**
 * @param {import("../data/mmu-loader.js").Mmu} mmu
 * @param {number} displayTypeInt
 * @returns {Concept}
 */
function attachDisplayType(mmu, displayTypeInt) {
  return /** @type {Concept} */ ({ ...mmu, displayType: displayTypeName(displayTypeInt) });
}

/** @type {(<T>(arr: T[]) => T[]) | null} */
let _shuffleOverride = null;

/**
 * Fisher–Yates shuffle with a test-only override hook. Tests pin to a
 * deterministic order without touching Math.random globally.
 *
 * @template T
 * @param {T[]} arr
 * @returns {T[]}
 */
function shuffle(arr) {
  if (_shuffleOverride) return _shuffleOverride(arr);
  const out = arr.slice();
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

/** Test-only: replace the shuffle implementation. Pass null to restore. */
export function _setShuffleForTest(/** @type {(<T>(arr: T[]) => T[]) | null} */ fn) {
  _shuffleOverride = fn;
}
